import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ArrowLeft, ArrowRightLeft, Check, ChevronDown, ClipboardList, FilePlus, LucideAngularModule, Plus, Search, Trash2, X } from 'lucide-angular';
import { Contract, Loa, LoaSchedule, Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { normalizeApplicationType } from '../application-type.util';

interface RouteOption {
  value: string;
  label: string;
}

interface LinkedParty {
  vendorId: string;
  loaId: string;
  quantum?: number | null;
}

interface ContractPartyMeta {
  sldc?: string | null;
  rldc?: string | null;
  injection_point?: string | null;
  is_embedded?: boolean | null;
}

interface AppFormState {
  contractId: string;
  applicationNumber: string;
  applicationType: string;
  applicationDate: string;
  gtamTransaction: 'Yes' | 'No';
  generationSource: string;
  corridors: string[];
  utrNumber: string;
  remarks1: string;
  remarks2: string;
  sellerVendorId: string;
  sellerLoaId: string;
  buyerVendorId: string;
  buyerLoaId: string;
}

interface ScheduleRow {
  id: number;
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  quantum: number;
  arrangement: number;
}

interface ScheduleFormState {
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  quantum: string | number;
  arrangement: string | number;
}

interface ApplicationScheduleApi {
  id?: string;
  application_id?: string;
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  quantum?: number | null;
}

interface ApprovalScheduleApi {
  schedule_from?: string | null;
  schedule_to?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  mwh?: number | string | null;
  requested_mw?: number | string | null;
  accepted_mw?: number | string | null;
  is_approved?: boolean | null;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id: string;
  application_date: string;
  status: string;
  total_quantum?: number | string | null;
  schedules?: ApplicationScheduleApi[];
  approval_schedules?: ApprovalScheduleApi[];
}

interface PunchedApplicationRow {
  date: string;
  quantum: number;
  status: string;
}

interface ContractScheduleSummaryRow {
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  quantum: number;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './application-form.page.html',
  styleUrl: './application-form.page.scss',
})
export class ApplicationFormPage implements OnInit {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly contractsService = inject(ContractsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly loaService = inject(LoaService);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);

  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly FilePlusIcon = FilePlus;
  protected readonly ArrowRightLeftIcon = ArrowRightLeft;
  protected readonly PlusIcon = Plus;
  protected readonly SearchIcon = Search;
  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly CheckIcon = Check;
  protected readonly XIcon = X;
  protected readonly Trash2Icon = Trash2;

  protected readonly applicationTypeOptions = ['DAYS-AHEAD', 'CONTINGENCY', 'FCFS', 'ADVANCE'];
  protected readonly generationSourceOptions = ['Solor', 'Non-Solor', 'Hydro'];
  protected readonly routesList: RouteOption[] = [
    { value: 'NR_WR', label: 'NR ↔ WR' },
    { value: 'NR_ER', label: 'NR ↔ ER' },
    { value: 'NR_NER', label: 'NR ↔ NER' },
    { value: 'WR_ER', label: 'WR ↔ ER' },
    { value: 'WR_SR', label: 'WR ↔ SR' },
    { value: 'ER_NER', label: 'ER ↔ NER' },
    { value: 'ER_SR', label: 'ER ↔ SR' },
    { value: 'NER_ER_NR', label: 'NER ↔ ER ↔ NR' },
    { value: 'NER_ER_WR', label: 'NER ↔ ER ↔ WR' },
    { value: 'NER_ER_SR', label: 'NER ↔ ER ↔ SR' },
    { value: 'NR_ER_SR', label: 'NR ↔ ER ↔ SR' },
    { value: 'NR_WR_SR', label: 'NR ↔ WR ↔ SR' },
    { value: 'ER_WR_SR', label: 'ER ↔ WR ↔ SR' },
    { value: 'NER_ER_NR_WR_SR', label: 'NER → ER → NR → WR → SR' },
    { value: 'SR_WR_NR_ER_NER', label: 'SR → WR → NR → ER → NER' },
    { value: 'ER_NR_WR_SR', label: 'ER → NR → WR → SR' },
    { value: 'WR_ER_NR', label: 'WR → ER → NR' },
    { value: 'NR_ER_NER', label: 'NR → ER → NER' },
  ];

  protected readonly contracts = signal<Contract[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly loas = signal<Loa[]>([]);
  protected readonly applications = signal<ApplicationApi[]>([]);
  protected readonly buyerLoaSchedules = signal<LoaSchedule[]>([]);
  protected readonly isSaving = signal(false);
  protected readonly generatedApplicationId = signal('');
  protected readonly scheduleRows = signal<ScheduleRow[]>([]);
  protected readonly scheduleModalOpen = signal(false);
  protected readonly editingScheduleId = signal<number | null>(null);
  protected readonly scheduleForm = signal<ScheduleFormState>({
    fromDate: '',
    toDate: '',
    fromTime: '',
    toTime: '',
    quantum: '',
    arrangement: '1',
  });

  protected readonly corridorOpen = signal(false);
  protected readonly corridorSearch = signal('');
  protected readonly contractDropdownOpen = signal(false);
  protected readonly contractSearch = signal('');

  protected readonly form = signal<AppFormState>({
    contractId: '',
    applicationNumber: '',
    applicationType: '',
    applicationDate: new Date().toISOString().slice(0, 10),
    gtamTransaction: 'No',
    generationSource: '',
    corridors: [],
    utrNumber: '',
    remarks1: '',
    remarks2: '',
    sellerVendorId: '',
    sellerLoaId: '',
    buyerVendorId: '',
    buyerLoaId: '',
  });

  protected readonly approvedContracts = computed(() =>
    [...this.contracts()]
      .filter((contract) => this.isContractApproved(contract))
      .sort((left, right) =>
        String(left.contract_number || '').localeCompare(String(right.contract_number || '')),
      ),
  );
  protected readonly filteredApprovedContracts = computed(() => {
    const term = this.contractSearch().trim().toLowerCase();
    if (!term) return this.approvedContracts();
    return this.approvedContracts().filter((contract) =>
      [
        contract.contract_number,
        contract.contract_type,
        contract.contract_category,
        contract.period_from,
        contract.period_to,
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  });

  protected readonly selectedContract = computed(() => this.approvedContracts().find((contract) => contract.id === this.form().contractId) || null);
  protected readonly selectedContractLabel = computed(() => this.selectedContract()?.contract_number || 'Select Approved Contract');
  protected readonly vendorMap = computed(() => new Map(this.vendors().map((vendor) => [vendor.id, vendor])));
  protected readonly loaMap = computed(() => new Map(this.loas().map((loa) => [loa.id, loa])));

  protected readonly sellerLinkedParties = computed(() => this.extractParties('seller'));
  protected readonly buyerLinkedParties = computed(() => this.extractParties('buyer'));

  protected readonly sellerVendorOptions = computed(() => this.uniqueVendors(this.sellerLinkedParties()));
  protected readonly buyerVendorOptions = computed(() => this.uniqueVendors(this.buyerLinkedParties()));

  protected readonly sellerLoaOptions = computed(() => {
    const selectedVendorId = this.form().sellerVendorId;
    if (!selectedVendorId) return [];
    return this.uniqueLoas(this.sellerLinkedParties().filter((item) => item.vendorId === selectedVendorId).map((item) => item.loaId));
  });

  protected readonly buyerLoaOptions = computed(() => {
    const selectedVendorId = this.form().buyerVendorId;
    if (!selectedVendorId) return [];
    return this.uniqueLoas(this.buyerLinkedParties().filter((item) => item.vendorId === selectedVendorId).map((item) => item.loaId));
  });

  protected readonly isGeneratorSourceMandatory = computed(() => this.form().gtamTransaction === 'Yes');

  protected readonly selectedBuyerQuantum = computed(() => {
    const buyerSchedule = this.preferredBuyerSchedule();
    const scheduleQuantum = buyerSchedule ? this.scheduleQuantumValue(buyerSchedule) : 0;
    if (scheduleQuantum > 0) return scheduleQuantum;

    const selectedVendorId = this.form().buyerVendorId;
    const selectedLoaId = this.form().buyerLoaId;
    const parties = this.buyerLinkedParties();
    const matchedParty =
      parties.find((party) => selectedLoaId && party.loaId === selectedLoaId) ||
      parties.find((party) => selectedVendorId && party.vendorId === selectedVendorId) ||
      parties[0];

    return Number(matchedParty?.quantum) || 0;
  });
  protected readonly selectedContractQuantum = computed(() => {
    return this.selectedBuyerQuantum();
  });
  protected readonly selectedContractQuantumMwh = computed(() => {
    const contract = this.selectedContract();
    if (!contract) return 0;
    return this.contractQuantumMwh(contract, this.selectedContractQuantum());
  });
  protected readonly punchedApplicationsForSelectedContract = computed<ApplicationApi[]>(() =>
    this.applications().filter(
      (app) =>
        app.contract_id === this.form().contractId &&
        (app.status || '').trim().toLowerCase() === 'punched',
    ),
  );
  protected readonly punchedApplicationsSummary = computed<PunchedApplicationRow[]>(() =>
    this.punchedApplicationsForSelectedContract().map((app) => ({
      date: app.application_date || '-',
      quantum: this.applicationScheduledQuantum(app),
      status: app.status || '-',
    })),
  );
  protected readonly sellerScheduleSummary = computed<ContractScheduleSummaryRow[]>(() =>
    this.punchedApplicationsForSelectedContract().flatMap((app) =>
      (app.schedules || []).map((row) => ({
        fromDate: row.from_date || '-',
        toDate: row.to_date || '-',
        fromTime: row.from_time || '-',
        toTime: row.to_time || '-',
        quantum: Number(row.quantum) || 0,
      })),
    ),
  );
  protected readonly buyerScheduleSummary = computed<ContractScheduleSummaryRow[]>(() =>
    this.punchedApplicationsForSelectedContract().flatMap((app) =>
      (app.approval_schedules || []).map((row) => ({
        fromDate: row.schedule_from || '-',
        toDate: row.schedule_to || '-',
        fromTime: row.start_time || '-',
        toTime: row.end_time || '-',
        quantum: this.approvalScheduleQuantum(row),
      })),
    ),
  );
  protected readonly alreadyPunchedQuantum = computed(() =>
    this.punchedApplicationsForSelectedContract().reduce((sum, app) => sum + this.applicationScheduledQuantum(app), 0),
  );
  protected readonly remainingToPunchQuantum = computed(() => Math.max(this.selectedContractQuantum() - this.alreadyPunchedQuantum(), 0));
  protected readonly alreadyPunchedQuantumMwh = computed(() =>
    this.punchedApplicationsForSelectedContract().reduce((sum, app) => sum + this.applicationScheduledQuantumMwh(app), 0),
  );
  protected readonly remainingToPunchQuantumMwh = computed(() =>
    Math.max(this.selectedContractQuantumMwh() - this.alreadyPunchedQuantumMwh(), 0),
  );
  protected readonly contractPunchProgressPercent = computed(() => {
    const total = this.selectedContractQuantumMwh();
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, (this.alreadyPunchedQuantumMwh() / total) * 100));
  });
  protected readonly currentScheduleQuantum = computed(() =>
    this.scheduleRows().reduce((sum, row) => sum + (Number(row.quantum) || 0), 0),
  );

  protected readonly filteredRoutes = computed(() => {
    const term = this.corridorSearch().trim().toLowerCase();
    if (!term) return this.routesList;
    return this.routesList.filter((route) => route.label.toLowerCase().includes(term) || route.value.toLowerCase().includes(term));
  });

  protected readonly selectedCorridorLabel = computed(() => {
    const selected = this.form().corridors;
    if (!selected.length) return 'Select Route(s)';
    if (selected.length === 1) {
      return this.routesList.find((route) => route.value === selected[0])?.label || selected[0];
    }
    return `${selected.length} routes selected`;
  });
  protected readonly canEditSchedule = computed(() => !this.generatedApplicationId());
  protected readonly timeOptions = this.buildTimeOptions();

  private pendingPunchRemainingQuantum = 0;
  private pendingPunchAutoApplied = false;

  ngOnInit(): void {
    const existingId = this.route.snapshot.paramMap.get('id');
    if (existingId) this.generatedApplicationId.set(existingId);
    const queryContractId = this.route.snapshot.queryParamMap.get('contractId') || '';
    const queryRemainingQuantum = Number(this.route.snapshot.queryParamMap.get('remainingQuantum') || '0');
    this.pendingPunchRemainingQuantum = Number.isFinite(queryRemainingQuantum) && queryRemainingQuantum > 0 ? queryRemainingQuantum : 0;

    this.contractsService.list().subscribe({
      next: (data) => {
        this.contracts.set(data || []);
        if (queryContractId) {
          const isAllowed = (data || []).some((item) => item.id === queryContractId && this.isContractApproved(item));
          if (isAllowed) this.onContractChange(queryContractId);
        }
      },
      error: () => this.toast.show('Failed to load contracts', '', 'danger'),
    });
    this.api.get<ApplicationApi[]>('/api/applications').subscribe({
      next: (data) => this.applications.set(data || []),
      error: () => this.applications.set([]),
    });
    this.vendorsService.list().subscribe({
      next: (data) => this.vendors.set(data || []),
      error: () => this.toast.show('Failed to load vendors', '', 'danger'),
    });
    this.loaService.list().subscribe({
      next: (data) => this.loas.set(data || []),
      error: () => this.toast.show('Failed to load LOAs', '', 'danger'),
    });
  }

  @HostListener('document:click')
  protected closeDropdownsOnOutsideClick(): void {
    this.corridorOpen.set(false);
    this.contractDropdownOpen.set(false);
  }

  protected patchForm(patch: Partial<AppFormState>): void {
    this.form.update((current) => ({ ...current, ...patch }));
  }

  protected partyMeta(kind: 'seller' | 'buyer'): ContractPartyMeta {
    const contractId = this.form().contractId;
    if (!contractId) return {};
    const contract = this.contracts().find((item) => item.id === contractId) || null;
    if (!contract) return {};

    const partyDetails = contract.party_details || [];
    if (!partyDetails.length) return {};

    const candidates = partyDetails.filter((party, index) => {
      return this.partyMatchesKind(party, kind, index);
    });

    const selectedVendorId = kind === 'buyer' ? this.form().buyerVendorId : this.form().sellerVendorId;
    const selectedLoaId = kind === 'buyer' ? this.form().buyerLoaId : this.form().sellerLoaId;

    const match =
      candidates.find((party) => selectedLoaId && party.loa_id === selectedLoaId) ||
      candidates.find((party) => selectedVendorId && party.vendor_id === selectedVendorId) ||
      candidates[0];

    return match || {};
  }

  protected formatEmbedded(value: boolean | null | undefined): string {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '-';
  }

  protected onContractChange(contractId: string): void {
    const contract = this.contracts().find((item) => item.id === contractId) || null;
    const partyDetails = contract?.party_details || [];
    const buyers = partyDetails.filter((party, index) => this.partyMatchesKind(party, 'buyer', index));
    const sellers = partyDetails.filter((party, index) => this.partyMatchesKind(party, 'seller', index));
    const defaultBuyer = buyers[0];
    const defaultSeller = sellers[0];

    const normalizedType = normalizeApplicationType(contract?.contract_type);
    const mappedType = this.applicationTypeOptions.includes(normalizedType) ? normalizedType : this.form().applicationType;

    this.patchForm({
      contractId,
      sellerVendorId: defaultSeller?.vendor_id || '',
      sellerLoaId: defaultSeller?.loa_id || '',
      buyerVendorId: defaultBuyer?.vendor_id || '',
      buyerLoaId: defaultBuyer?.loa_id || '',
      applicationType: mappedType,
    });
    this.loadBuyerLoaSchedules(defaultBuyer?.loa_id || '');
    this.contractSearch.set(contract?.contract_number || '');

    if (!this.pendingPunchAutoApplied && this.pendingPunchRemainingQuantum > 0 && contract) {
      this.scheduleRows.set([this.buildPendingPunchSchedule(contract, this.pendingPunchRemainingQuantum)]);
      this.pendingPunchAutoApplied = true;
    }
  }

  protected onGtamChange(value: 'Yes' | 'No'): void {
    this.patchForm({
      gtamTransaction: value,
      generationSource: value === 'Yes' ? this.form().generationSource : '',
    });
  }

  protected toggleCorridorDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.corridorOpen.set(!this.corridorOpen());
  }

  protected toggleContractDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.contractDropdownOpen.set(!this.contractDropdownOpen());
  }

  protected onContractSearchChange(value: string): void {
    this.contractSearch.set(value);
  }

  protected onContractPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected selectContract(contractId: string): void {
    this.onContractChange(contractId);
    this.contractDropdownOpen.set(false);
  }

  protected onCorridorSearchChange(value: string): void {
    this.corridorSearch.set(value);
  }

  protected onCorridorPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected isCorridorSelected(value: string): boolean {
    return this.form().corridors.includes(value);
  }

  protected toggleCorridor(value: string, checked: boolean): void {
    const current = this.form().corridors;
    if (checked && !current.includes(value)) {
      this.patchForm({ corridors: [...current, value] });
      return;
    }
    if (!checked) {
      this.patchForm({ corridors: current.filter((item) => item !== value) });
    }
  }

  protected onSellerVendorChange(vendorId: string): void {
    this.patchForm({ sellerVendorId: vendorId, sellerLoaId: '' });
  }

  protected onBuyerVendorChange(vendorId: string): void {
    this.patchForm({ buyerVendorId: vendorId, buyerLoaId: '' });
    this.loadBuyerLoaSchedules('');
  }

  protected onBuyerLoaChange(loaId: string): void {
    this.patchForm({ buyerLoaId: loaId });
    this.loadBuyerLoaSchedules(loaId);
  }

  protected submit(): void {
    if (this.isSaving()) return;
    const form = this.form();
    if (!form.contractId) {
      this.toast.show('Contract is required', '', 'danger');
      return;
    }
    if (!form.applicationNumber.trim()) {
      this.toast.show('Application No is required', '', 'danger');
      return;
    }
    if (!form.applicationType) {
      this.toast.show('Application Type is required', '', 'danger');
      return;
    }
    if (!form.applicationDate) {
      this.toast.show('Application Date is required', '', 'danger');
      return;
    }
    if (form.gtamTransaction === 'Yes' && !form.generationSource) {
      this.toast.show('Generator Source is mandatory when GTAM is Yes', '', 'danger');
      return;
    }
    if (!form.corridors.length) {
      this.toast.show('Select at least one corridor route', '', 'danger');
      return;
    }
    if (!form.utrNumber.trim()) {
      this.toast.show('UTR Number is required', '', 'danger');
      return;
    }
    if (this.scheduleRows().length === 0) {
      this.toast.show('Please add at least one schedule before saving', '', 'danger');
      return;
    }
    if (this.currentScheduleQuantum() > this.remainingToPunchQuantum()) {
      this.toast.show('Application quantum cannot be greater than remaining quantum', '', 'danger');
      return;
    }

    const payload = {
      application_number: form.applicationNumber.trim(),
      contract_id: form.contractId,
      application_type: normalizeApplicationType(form.applicationType),
      application_date: form.applicationDate,
      route: form.corridors.join(','),
      gtam_transaction: form.gtamTransaction === 'Yes',
      generation_source: form.generationSource || null,
      utr_number: form.utrNumber.trim(),
      remarks_format_1: form.remarks1 || null,
      remarks_format_2: form.remarks2 || null,
      seller_vendor_id: form.sellerVendorId || null,
      seller_loa_id: form.sellerLoaId || null,
      buyer_vendor_id: form.buyerVendorId || null,
      buyer_loa_id: form.buyerLoaId || null,
      total_quantum: this.currentScheduleQuantum(),
      approved_quantum: 0,
      status: 'punched',
      schedules: this.scheduleRows().map((row) => ({
        from_date: row.fromDate,
        to_date: row.toDate,
        from_time: row.fromTime,
        to_time: row.toTime,
        quantum: row.quantum,
      })),
    };

    this.isSaving.set(true);
    this.api.post<{ id: string }>('/api/applications', payload).subscribe({
      next: (response) => {
        this.generatedApplicationId.set(response?.id || '');
        this.toast.show('Application created successfully', '', 'success');
      },
      error: (error: HttpErrorResponse) => {
        const detail = (error.error?.detail || '').toString().trim();
        this.toast.show(detail || 'Failed to create application', '', 'danger');
      },
      complete: () => this.isSaving.set(false),
    });
  }

  protected generateFormatD(): void {
    const id = this.generatedApplicationId();
    if (!id) {
      this.toast.show('Save and punch application first', '', 'danger');
      return;
    }
    window.open(this.api.resolve(`/api/applications/${id}/format-d`), '_blank');
  }

  protected openScheduleModal(): void {
    if (!this.canEditSchedule()) return;
    this.editingScheduleId.set(null);
    const contract = this.selectedContract();
    const buyerSchedule = this.preferredBuyerSchedule();
    this.scheduleForm.set({
      fromDate: buyerSchedule?.from_date || contract?.period_from || '',
      toDate: buyerSchedule?.to_date || contract?.period_to || '',
      fromTime: buyerSchedule?.from_time || '00:00',
      toTime: buyerSchedule?.to_time || '24:00',
      quantum: this.defaultScheduleQuantum(buyerSchedule),
      arrangement: String(this.scheduleArrangementValue(buyerSchedule) || 1),
    });
    this.scheduleModalOpen.set(true);
  }

  protected closeScheduleModal(): void {
    this.scheduleModalOpen.set(false);
    this.editingScheduleId.set(null);
  }

  protected patchScheduleForm(patch: Partial<ScheduleFormState>): void {
    this.scheduleForm.update((current) => {
      const next = { ...current, ...patch };
      const arrangementChanged = Object.prototype.hasOwnProperty.call(patch, 'arrangement');
      if (!arrangementChanged) return next;

      const buyerSchedule = this.findBuyerScheduleByArrangement(next.arrangement);
      if (!buyerSchedule) return next;

      return {
        ...next,
        quantum: this.defaultScheduleQuantum(buyerSchedule),
        fromDate: buyerSchedule.from_date || next.fromDate,
        toDate: buyerSchedule.to_date || next.toDate,
        fromTime: buyerSchedule.from_time || next.fromTime,
        toTime: buyerSchedule.to_time || next.toTime,
      };
    });
  }

  protected addSchedule(): void {
    if (!this.canEditSchedule()) return;
    const form = this.scheduleForm();
    const quantumText = String(form.quantum ?? '').trim();
    const arrangementText = String(form.arrangement ?? '').trim();
    if (!form.fromDate || !form.toDate || !form.fromTime || !form.toTime || !quantumText) {
      this.toast.show('All schedule fields are required', '', 'danger');
      return;
    }
    const fromTimeMinutes = this.toMinutes(form.fromTime);
    const toTimeMinutes = this.toMinutes(form.toTime);
    if (fromTimeMinutes < 0 || toTimeMinutes < 0 || toTimeMinutes <= fromTimeMinutes) {
      this.toast.show('To Time should be greater than From Time', '', 'danger');
      return;
    }
    if (Number(quantumText) <= 0) {
      this.toast.show('Quantum should be greater than 0', '', 'danger');
      return;
    }
    if (Number(arrangementText || '1') <= 0) {
      this.toast.show('Arrangement should be greater than 0', '', 'danger');
      return;
    }
    const matchedBuyerSchedule = this.findBuyerScheduleByArrangement(arrangementText);
    const resolvedQuantum = matchedBuyerSchedule ? this.scheduleQuantumValue(matchedBuyerSchedule) : Number(quantumText);
    const payload: ScheduleRow = {
      id: this.editingScheduleId() ?? Date.now(),
      fromDate: matchedBuyerSchedule?.from_date || form.fromDate,
      toDate: matchedBuyerSchedule?.to_date || form.toDate,
      fromTime: matchedBuyerSchedule?.from_time || form.fromTime,
      toTime: matchedBuyerSchedule?.to_time || form.toTime,
      quantum: resolvedQuantum,
      arrangement: Number(arrangementText || '1'),
    };
    const nextTotalQuantum = this.scheduleRows()
      .filter((item) => item.id !== payload.id)
      .reduce((sum, item) => sum + (Number(item.quantum) || 0), 0) + payload.quantum;
    if (nextTotalQuantum > this.remainingToPunchQuantum()) {
      this.toast.show('Application quantum cannot be greater than remaining quantum', '', 'danger');
      return;
    }
    if (this.editingScheduleId() === null) {
      this.scheduleRows.update((current) => [...current, payload]);
    } else {
      this.scheduleRows.update((current) => current.map((item) => (item.id === payload.id ? payload : item)));
    }
    this.closeScheduleModal();
  }

  private isContractApproved(contract: Contract): boolean {
    const primaryStatus = String(contract.status || '').toLowerCase().trim();
    if (primaryStatus === 'approved')return true
    return false;
  }

  protected editSchedule(id: number): void {
    if (!this.canEditSchedule()) return;
    const row = this.scheduleRows().find((item) => item.id === id);
    if (!row) return;
    this.editingScheduleId.set(row.id);
    this.scheduleForm.set({
      fromDate: row.fromDate,
      toDate: row.toDate,
      fromTime: row.fromTime,
      toTime: row.toTime,
      quantum: row.quantum,
      arrangement: row.arrangement,
    });
    this.scheduleModalOpen.set(true);
  }

  protected removeSchedule(id: number): void {
    if (!this.canEditSchedule()) return;
    this.scheduleRows.update((current) => current.filter((item) => item.id !== id));
  }

  protected formatContractDate(value: string | null | undefined): string {
    const raw = String(value || '').trim();
    if (!raw) return '-';
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${day}-${month}-${year}`;
    }
    return raw;
  }

  private applicationScheduledQuantum(application: ApplicationApi): number {
    const fromSchedules = (application.schedules || []).reduce((sum, row) => sum + (Number(row.quantum) || 0), 0);
    if (fromSchedules > 0) return fromSchedules;
    return Number(application.total_quantum) || 0;
  }

  private applicationScheduledQuantumMwh(application: ApplicationApi): number {
    const schedules = application.schedules || [];
    const totalFromSchedules = schedules.reduce((sum, row) => {
      const quantum = Number(row.quantum) || 0;
      if (quantum <= 0) return sum;

      const dayDifference = this.inclusiveDayCount(row.from_date, row.to_date || row.from_date);
      const hourDifference = this.timeDifferenceHours(row.from_time, row.to_time);
      if (dayDifference <= 0 || hourDifference <= 0) return sum;

      return sum + dayDifference * hourDifference * quantum * 250;
    }, 0);
    if (totalFromSchedules > 0) return totalFromSchedules;

    const fallbackQuantum = Number(application.total_quantum) || 0;
    if (fallbackQuantum <= 0 || !schedules.length) return fallbackQuantum;

    const firstRow = schedules[0];
    const dayDifference = this.inclusiveDayCount(firstRow.from_date, firstRow.to_date || firstRow.from_date);
    const hourDifference = this.timeDifferenceHours(firstRow.from_time, firstRow.to_time);
    if (dayDifference <= 0 || hourDifference <= 0) return fallbackQuantum;

    return dayDifference * hourDifference * fallbackQuantum * 250;
  }

  private approvalScheduleQuantum(row: ApprovalScheduleApi): number {
    return Number(row.accepted_mw ?? row.requested_mw ?? row.mwh) || 0;
  }

  private contractQuantumMwh(contract: Contract, quantumMw: number): number {
    if (quantumMw <= 0) return 0;
    const dayDifference = this.inclusiveDayCount(contract.period_from, contract.period_to);
    const hourDifference = 24;
    if (dayDifference <= 0) return 0;
    return dayDifference * hourDifference * quantumMw * 250;
  }

  private buildPendingPunchSchedule(contract: Contract, quantum: number): ScheduleRow {
    return {
      id: Date.now(),
      fromDate: contract.period_from,
      toDate: contract.period_to,
      fromTime: '00:00',
      toTime: '24:00',
      quantum: Math.min(quantum, this.remainingToPunchQuantum()),
      arrangement: 1,
    };
  }

  private loadBuyerLoaSchedules(loaId: string): void {
    if (!loaId) {
      this.buyerLoaSchedules.set([]);
      return;
    }
    this.loaService.listSchedules(loaId).subscribe({
      next: (items) => this.buyerLoaSchedules.set((items || []).filter((item) => this.isBuyerScheduleDirection(item))),
      error: () => {
        this.buyerLoaSchedules.set([]);
        this.toast.show('Failed to load buyer LOA schedules', '', 'danger');
      },
    });
  }

  private preferredBuyerSchedule(): LoaSchedule | null {
    const schedules = [...this.buyerLoaSchedules()].sort((left, right) => this.scheduleArrangementValue(left) - this.scheduleArrangementValue(right));
    return schedules[0] || null;
  }

  private findBuyerScheduleByArrangement(arrangement: string | number): LoaSchedule | null {
    const target = Number(arrangement);
    if (!Number.isFinite(target) || target <= 0) return null;
    return this.buyerLoaSchedules().find((item) => this.scheduleArrangementValue(item) === target) || null;
  }

  private defaultScheduleQuantum(schedule: LoaSchedule | null | undefined): number | '' {
    if (!schedule) return this.remainingToPunchQuantum() || '';
    const quantum = this.scheduleQuantumValue(schedule);
    if (quantum <= 0) return this.remainingToPunchQuantum() || '';
    return Math.min(quantum, this.remainingToPunchQuantum() || quantum);
  }

  private scheduleQuantumValue(schedule: LoaSchedule): number {
    return Number(schedule.quantum_mw) || 0;
  }

  private scheduleArrangementValue(schedule: LoaSchedule | null | undefined): number {
    const arrangement = Number(schedule?.arrangement);
    return Number.isFinite(arrangement) && arrangement > 0 ? arrangement : 1;
  }

  private isBuyerScheduleDirection(schedule: LoaSchedule): boolean {
    const direction = String(schedule.direction || '').trim().toLowerCase();
    return !direction || direction === 'import';
  }

  private buildTimeOptions(): string[] {
    const items: string[] = [];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 15) {
        items.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      }
    }
    items.push('24:00');
    return items;
  }

  private toMinutes(value: string): number {
    if (value === '24:00') return 24 * 60;
    const [hours, minutes] = value.split(':').map((part) => Number(part));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return -1;
    return hours * 60 + minutes;
  }

  private inclusiveDayCount(fromDate: string, toDate: string): number {
    const from = this.parseDateOnly(fromDate);
    const to = this.parseDateOnly(toDate);
    if (!from || !to) return 0;
    const diffMs = to.getTime() - from.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  }

  private timeDifferenceHours(fromTime: string, toTime: string): number {
    const fromMinutes = this.toMinutes(fromTime);
    const toMinutes = this.toMinutes(toTime);
    if (fromMinutes < 0 || toMinutes < 0 || toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseDateOnly(value: string): Date | null {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  private extractParties(kind: 'seller' | 'buyer'): LinkedParty[] {
    const contract = this.selectedContract();
    if (!contract?.party_details?.length) return [];
    const parties = contract.party_details;
    const result: LinkedParty[] = [];
    parties.forEach((party, index) => {
      const matchesByType = this.partyMatchesKind(party, kind, index);
      if (!matchesByType) return;
      result.push({
        vendorId: party.vendor_id,
        loaId: party.loa_id,
        quantum: Number(party.quantum) || 0,
      });
    });

    const seen = new Set<string>();
    return result.filter((item) => {
      const key = `${item.vendorId}::${item.loaId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private uniqueVendors(parties: LinkedParty[]): Vendor[] {
    const ids = new Set(parties.map((party) => party.vendorId));
    return Array.from(ids)
      .map((id) => this.vendorMap().get(id))
      .filter((vendor): vendor is Vendor => !!vendor);
  }

  private uniqueLoas(loaIds: string[]): Loa[] {
    const ids = new Set(loaIds);
    return Array.from(ids)
      .map((id) => this.loaMap().get(id))
      .filter((loa): loa is Loa => !!loa);
  }

  private partyMatchesKind(
    party: { loa_id: string; party_type?: string | null },
    kind: 'seller' | 'buyer',
    index: number,
  ): boolean {
    const loa = this.loaMap().get(String(party.loa_id || '').trim()) || null;
    const loaCategory = String(loa?.category || '').trim().toLowerCase();
    if (loaCategory.includes('buy') || loaCategory.includes('purchase')) return kind === 'buyer';
    if (loaCategory.includes('sell') || loaCategory.includes('sale')) return kind === 'seller';

    const partyType = String(party.party_type || '').trim().toLowerCase();
    if (partyType.includes('buyer') || partyType.includes('buy')) return kind === 'buyer';
    if (partyType.includes('seller') || partyType.includes('sell')) return kind === 'seller';

    return kind === 'buyer' ? index % 2 === 0 : index % 2 === 1;
  }
}
